import React, { useEffect, useMemo, useState } from 'react';
import { toast } from '../../../components/shared/toast';
import { apiClient } from '../../../api/apiClient';
import { GET_ARTIST_SONGS_URL } from '../../../api/artist';
import LoadingOverlay from '../../../components/shared/LoadingOverlay';
import Pagination from '../../../components/shared/Pagination';
import en from '../../../translations/en.json';
import { collectArtistDocuments, isActiveArtistSong } from './artistTabUtils';

function DocumentsTab({ artist }) {
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [activeSongs, setActiveSongs] = useState([]);

  const t = (key) => {
    return key.split('.').reduce((obj, i) => (obj ? obj[i] : null), en) || key;
  };

  useEffect(() => {
    let isMounted = true;

    const loadArtistDocuments = async () => {
      if (!artist?.id) {
        if (isMounted) setDocuments([]);
        return;
      }

      try {
        setIsLoading(true);
        setPage(1);
        const songsRes = await apiClient(`${GET_ARTIST_SONGS_URL(artist.id)}?page=1&limit=500`);
        const rawSongs = Array.isArray(songsRes?.songs)
          ? songsRes.songs
          : Array.isArray(songsRes?.data)
            ? songsRes.data
            : [];

        const activeSongsOnly = rawSongs.filter(isActiveArtistSong);
        if (isMounted) setActiveSongs(activeSongsOnly);

        const songIds = activeSongsOnly.map((song) => song?.id || song?.song_id || song?.songId).filter(Boolean);
        const aggregatedDocs = [];

        if (songIds.length) {
          await Promise.all(songIds.map(async (songId) => {
            try {
              const ownership = await apiClient(`/songs/${songId}/ownership`);
              if (ownership) {
                const docsForSong = collectArtistDocuments([{ ...activeSongsOnly.find((song) => (song?.id || song?.song_id || song?.songId) === songId), ownership }], { [songId]: ownership });
                aggregatedDocs.push(...docsForSong);
              }
            } catch (err) {
              console.error(`Failed to fetch ownership for song ${songId}:`, err);
            }
          }));
        }

        if (isMounted) setDocuments(aggregatedDocs);
      } catch (err) {
        console.error('Failed to load artist documents:', err);
        if (isMounted) {
          setDocuments([]);
          toast.error('Failed to load artist documents.');
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadArtistDocuments();
    return () => {
      isMounted = false;
    };
  }, [artist?.id]);

  const pagedDocuments = useMemo(() => documents.slice((page - 1) * 20, page * 20), [documents, page]);

  return (
    <div className="artist-documents-tab" style={{ position: 'relative' }}>
      <LoadingOverlay isLoading={isLoading} message="Loading documents from the artist songs..." />
      <div className="docs-tab-controls-row">
        <span className="docs-count-summary-label">
          {documents.length} {t('artistDetails.docsCountSummary')}
        </span>

        <div className="docs-controls-right">
          <div className="docs-search-wrapper">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" className="search-icon">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input type="text" placeholder={t('artistDetails.docsSearchPlaceholder')} className="docs-search-input" />
          </div>
        </div>
      </div>

      <div className="docs-cards-grid">
        {!isLoading && pagedDocuments.length > 0 ? (
          <>
            {pagedDocuments.map((doc) => (
              <div key={doc.id} className="doc-card">
                <div className="doc-card-header">
                  <button type="button" className="doc-menu-btn" aria-label="Document options">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <circle cx="12" cy="12" r="1" />
                      <circle cx="12" cy="5" r="1" />
                      <circle cx="12" cy="19" r="1" />
                    </svg>
                  </button>
                </div>

                <div className="doc-pdf-icon-body">
                  <div className="pdf-badge-icon">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                    <span className="pdf-text-label">PDF</span>
                  </div>
                </div>

                <div className="doc-card-body">
                  <h4 className="doc-card-name" title={doc.name}>{doc.name}</h4>
                  <span className="doc-type-badge">{doc.type}</span>
                  <div className="doc-meta-row">
                    {doc.date ? <><span className="doc-date">{doc.date}</span><span className="doc-bullet">&bull;</span></> : null}
                    <span className="doc-size">{doc.size}</span>
                  </div>
                  {doc.songName ? <div className="doc-meta-row" style={{ marginTop: '6px' }}><span className="doc-size">{doc.songName}</span></div> : null}
                </div>

                <div className="doc-card-split-footer">
                  <button type="button" className="split-footer-btn view-btn" onClick={() => toast.info(`Viewing document: ${doc.name}`)}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                    <span>{t('artistDetails.docsViewBtn')}</span>
                  </button>
                </div>
              </div>
            ))}
            <div style={{ gridColumn: '1 / -1' }}>
              <Pagination currentPage={page} totalPages={Math.max(1, Math.ceil(documents.length / 20))} totalCount={documents.length} onPageChange={setPage} />
            </div>
          </>
        ) : (
          <div className="empty-state-card" style={{ gridColumn: '1 / -1', padding: '24px' }}>
            <p style={{ margin: 0, color: '#64748b' }}>No documents found for the artist's active songs.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default DocumentsTab;
